import React from "react";
import { Lead } from "../../types";
import { Package2, Phone, Info, AlertTriangle } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatPhoneWithLeadingZero } from "../../utils/phoneFormatter";
import { getLeadDisplayAddressColumn } from "../../utils/leadAddress";

/** Returns whether the lead is problematic, full reason (tooltip), one-line reason, and reason count (extra line only when multiple). */
function getProblematicPreview(lead: Lead | any, t: (k: string) => string): { isProblematic: boolean; reasonText: string; reasonLine: string; reasonsCount: number } {
  const reasons: string[] = [];
  if (lead.abusiveCommunication) reasons.push(t("leads.abusiveCommunicationLabel"));
  if (lead.redFlags?.length) reasons.push(...lead.redFlags.map((f: any) => f.reason || f.type).filter(Boolean));
  if (lead.problematicReason) {
    const key = "leads.reason" + String(lead.problematicReason).charAt(0).toUpperCase() + String(lead.problematicReason).slice(1);
    reasons.push(t(key) || String(lead.problematicReason));
  }
  const isProblematic = reasons.length > 0;
  const reasonLine = reasons.join("; ");
  const reasonText = isProblematic ? `${t("leads.problematic")}: ${reasonLine}` : "";
  return { isProblematic, reasonText, reasonLine, reasonsCount: reasons.length };
}

const BUSY_LINE_MS = 30 * 60 * 1000;
const NO_ANSWER_MS = 2 * 60 * 60 * 1000;
const UNREACHABLE_MS = 24 * 60 * 60 * 1000;

/** Format date string (YYYY-MM-DD or ISO) to dd-mm-yyyy. */
function formatNextCallDateDdMmYyyy(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/** Format a Date to "dd-mm-yyyy HH:mm" for tooltip. */
function formatAvailableAt(d: Date): string {
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${h}:${m}`;
}

/** Last call action from lead (previousCallActions, operatorActions, or callCenterCompletedAt + callOutcome). */
function getLastCallAction(lead: Lead): { at: Date; callOutcome: string } | null {
  const actions = lead.previousCallActions;
  if (actions && Array.isArray(actions) && actions.length > 0) {
    const last = actions[actions.length - 1];
    const at = typeof last.at === "string" ? new Date(last.at) : last.at;
    if (last.callOutcome) return { at, callOutcome: last.callOutcome };
  }
  const opActions = lead.operatorActions;
  if (opActions && Array.isArray(opActions)) {
    for (let i = opActions.length - 1; i >= 0; i--) {
      const e = opActions[i];
      if (e.action === "end_call" && e.callOutcome) {
        const at = typeof e.at === "string" ? new Date(e.at) : e.at;
        return { at, callOutcome: e.callOutcome };
      }
    }
  }
  if (lead.callCenterCompletedAt && lead.callOutcome) {
    const at = typeof lead.callCenterCompletedAt === "string" ? new Date(lead.callCenterCompletedAt) : new Date(lead.callCenterCompletedAt as Date);
    return { at, callOutcome: lead.callOutcome };
  }
  return null;
}

/**
 * When the lead becomes available for Call Again (by outcome + time rules).
 * Returns null if available now, otherwise the Date from which it's available.
 * If last order needs confirmation, Call Again is locked until the day after confirmOnDate.
 */
function getCallAgainAvailableAt(lead: Lead): Date | null {
  if (lead.lastOrderNeedsConfirmation && lead.lastOrderConfirmOnDate) {
    const fd = String(lead.lastOrderConfirmOnDate).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(fd)) {
      return new Date(fd + "T00:00:00"); // Call Again available from the same day (start of confirm date)
    }
  }
  const last = getLastCallAction(lead);
  if (!last) return null;
  const outcome = (last.callOutcome || "").toLowerCase();
  const at = last.at instanceof Date ? last.at : new Date(last.at);
  if (outcome === "busy_line") {
    return new Date(at.getTime() + BUSY_LINE_MS);
  }
  if (outcome === "no_answer") {
    return new Date(at.getTime() + NO_ANSWER_MS);
  }
  if (outcome === "unreachable") {
    return new Date(at.getTime() + UNREACHABLE_MS);
  }
  if (outcome === "callback") {
    const fd = lead.followUpDate ? String(lead.followUpDate).slice(0, 10) : "";
    if (!fd) return null;
    const hours = (lead.followUpHours || lead.bestTimeToCall || "00:00").trim().slice(0, 5);
    const availableAt = new Date(`${fd}T${hours}:00`);
    if (isNaN(availableAt.getTime())) return null;
    return availableAt;
  }
  return null;
}

interface MyLeadsTableProps {
  orders: Lead[]; // Changed from Order[] to Lead[] - keeping prop name for compatibility
  onEditLead?: (lead: Lead) => void;
  onViewLead?: (lead: Lead) => void;
  currentUser?: {
    id: string;
    username: string;
    role?: string;
  };
}

const MyLeadsTable: React.FC<MyLeadsTableProps> = ({ orders, onEditLead, onViewLead, currentUser }) => {
  const { t } = useLanguage();

  // Memoized row component
  const LeadRow = React.memo(
    ({
      lead,
      t: tFn,
    }: {
      lead: Lead;
      t: (k: string) => string;
    }) => {
      // Check if call has been ended (has callOutcome or callCenterCompletedAt)
      const isCallEnded = !!(lead.callOutcome || lead.callCenterCompletedAt);

      // Allow call-again if leadOwner matches current user (even if call is completed)
      const canEdit = currentUser && lead.leadOwner && lead.leadOwner.userId === currentUser.id;

      // Get call outcome display
      const getCallOutcomeDisplay = () => {
        if (!lead.callOutcome) return null;
        
        const outcomeMap: { [key: string]: { label: string; color: string } } = {
          'successful': { label: tFn('leads.callOutcomeSuccessful'), color: 'bg-green-100 text-green-800' },
          'successful_with_confirmation': { label: tFn('leads.successfulWithConfirmation'), color: 'bg-green-100 text-green-800' },
          'no_answer': { label: tFn('leads.callOutcomeNoAnswer'), color: 'bg-yellow-100 text-yellow-800' },
          'busy_line': { label: tFn('leads.busyLine'), color: 'bg-amber-100 text-amber-800' },
          'wrong_number': { label: tFn('leads.wrongNumber'), color: 'bg-orange-100 text-orange-800' },
          'unreachable': { label: tFn('leads.unreachable'), color: 'bg-orange-100 text-orange-800' },
          'number_doesnt_exist': { label: tFn('leads.numberDoesntExist'), color: 'bg-orange-100 text-orange-800' },
          'callback': { label: tFn('leads.callOutcomeCallback'), color: 'bg-blue-100 text-blue-800' },
          'not_interested': { label: tFn('leads.callOutcomeNotInterested'), color: 'bg-red-100 text-red-800' },
          'other': { label: tFn('leads.callOutcomeOther'), color: 'bg-gray-100 text-gray-800' }
        };
        
        const outcome = outcomeMap[lead.callOutcome] || { label: lead.callOutcome, color: 'bg-gray-100 text-gray-800' };
        const showRejectedOrConfirmed = lead.callOutcome === 'successful_with_confirmation' && !lead.lastOrderNeedsConfirmation;
        return (
          <div className="flex flex-col gap-2">
            <span className={`inline-flex items-center w-fit px-2.5 py-0.5 rounded-full text-xs font-medium ${outcome.color}`}>
              {outcome.label}
            </span>
            {showRejectedOrConfirmed && (
              <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded text-xs font-medium ${lead.lastOrderRejected ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                {lead.lastOrderRejected ? tFn('callCenter.confirmationRejected') : tFn('callCenter.confirmationSuccess')}
              </span>
            )}
          </div>
        );
      };

      const hasFollowUpExtra = (lead.followUpNotes && lead.followUpNotes.trim()) || (lead.followUpPotentialProduct && lead.followUpPotentialProduct.trim());
      const callAgainAvailableAt = getCallAgainAvailableAt(lead);
      const isCallAgainLocked = !!(canEdit && callAgainAvailableAt && new Date() < callAgainAvailableAt);

      const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onEditLead && canEdit && !isCallAgainLocked) {
          onEditLead(lead);
        }
      };

      return (
        <tr className="hover:bg-gray-50 transition-colors">
          <td className="px-6 py-4">
            {(() => {
              const preview = getProblematicPreview(lead, tFn);
              return (
                <div className="text-sm font-medium text-gray-900 break-words">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{lead.name || "N/A"}</span>
                    {preview.isProblematic && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
                        title={preview.reasonText}
                      >
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        {tFn("leads.problematic")}
                      </span>
                    )}
                  </div>
                  {preview.isProblematic && preview.reasonLine && preview.reasonsCount > 1 && (
                    <div className="mt-0.5 text-xs text-amber-800">
                      {tFn("leads.problematicBecause")}: {preview.reasonLine}
                    </div>
                  )}
                </div>
              );
            })()}
          </td>
          <td className="px-6 py-4">
            <div className="text-sm text-gray-900 break-words font-mono">
              {formatPhoneWithLeadingZero(lead.phone)}
            </div>
          </td>
          <td className="px-6 py-4" style={{ maxWidth: "300px" }}>
            <div className="text-sm text-gray-900 break-words">
              {getLeadDisplayAddressColumn(lead) || "No address"}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {lead.callCenterCompletedBy ? (
              <div>
                <div className="font-medium text-gray-900">
                  {lead.callCenterCompletedBy.username}
                </div>
                {lead.callCenterCompletedAt && (
                  <div className="text-xs text-gray-400">
                    {new Date(lead.callCenterCompletedAt).toLocaleString()}
                  </div>
                )}
              </div>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </td>
          <td className="px-6 py-4 text-sm" style={{ minWidth: "220px" }}>
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="min-w-0">{getCallOutcomeDisplay() || <span className="text-gray-400">—</span>}</span>
              {lead.lastOrderNeedsConfirmation && lead.lastOrderConfirmOnDate && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title={tFn("callCenter.needConfirmationUntil")}>
                  {tFn("callCenter.needConfirmation")} {formatNextCallDateDdMmYyyy(lead.lastOrderConfirmOnDate)}
                </span>
              )}
              {lead.callOutcome === "callback" && (hasFollowUpExtra || lead.followUpDate) && (
                <div className="relative inline-flex group items-center shrink-0 ml-auto">
                  <span className="inline-flex items-center justify-center w-4 h-4 max-w-[24px] max-h-[24px] rounded-full bg-blue-100 text-blue-600 cursor-help shrink-0">
                    <Info className="w-3 h-3" />
                  </span>
                  <div className="absolute left-full top-0 ml-1 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto z-30 w-52 min-w-52 px-2.5 py-2 text-xs rounded-lg shadow-lg border border-gray-200 bg-white text-left transition-opacity">
                    <div className="font-medium text-gray-600 pb-1 border-b border-gray-100">
                      {tFn("callCenter.followUp")}
                    </div>
                    <div className="space-y-1 mt-1">
                      {lead.followUpDate && (
                        <div>
                          <span className="text-gray-500">{tFn("callCenter.nextCallDate")}: </span>
                          {formatNextCallDateDdMmYyyy(lead.followUpDate)}
                          {lead.followUpHours ? ` ${lead.followUpHours}` : ""}
                        </div>
                      )}
                      {lead.followUpNotes && lead.followUpNotes.trim() && (
                        <div className="break-words">
                          <span className="text-gray-500">{tFn("callCenter.followUpMessage")}: </span>
                          {lead.followUpNotes.trim()}
                        </div>
                      )}
                      {lead.followUpPotentialProduct && lead.followUpPotentialProduct.trim() && (
                        <div className="break-words">
                          <span className="text-gray-500">{tFn("callCenter.nextCallProduct")}: </span>
                          {lead.followUpPotentialProduct.trim()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            {canEdit && onEditLead ? (
              <button
                onClick={handleEditClick}
                disabled={isCallAgainLocked}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[#264a3e] hover:bg-[#1a3329] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#264a3e] transition-colors"
                title={isCallAgainLocked && callAgainAvailableAt ? `${tFn("callCenter.callAgainAvailableFrom")} ${formatAvailableAt(callAgainAvailableAt)}` : tFn("callCenter.callAgain")}
              >
                <Phone className="w-3 h-3 mr-1.5" />
                {tFn("callCenter.callAgain")}
              </button>
            ) : (
              <span className="text-gray-400 text-xs">—</span>
            )}
          </td>
        </tr>
      );
    }
  );

  if (orders.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="flex justify-center mb-4">
          <Package2 className="w-16 h-16 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No Leads Assigned
        </h3>
        <p className="text-gray-600">
          You don't have any leads assigned yet. Take leads from the New Leads
          Pool to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <table className="w-full min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Customer Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Phone
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              style={{ maxWidth: "300px" }}
            >
              Address
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Last Call
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: "220px" }}>
              {t('callCenter.lastEndCallStatus')}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {orders.map((lead) => (
            <LeadRow key={lead.id} lead={lead} t={t} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MyLeadsTable;

